import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExerciseType } from '@org/model';
import { formatAcceptedAnswers } from '@org/exercises';
import { cn } from '../../../../utils';
import type { ContentDisplayVariant } from '../../hooks/content-renderer-types';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import type {
  ExerciseFeedbackMessage,
  ExerciseSubmitHandler,
} from '../../hooks/content-renderer-types';
import { ContentDescription } from '../shared/content-description';
import { renderInlineMarkdown } from '../../../custom/markdown';
import { AnswerCorrection, Blank, WorkbookPanel, ZineButton } from '../../zine';
import type { AnswerCheckState } from '../../zine';
import {
  splitWorkbookFeedback,
  WorkbookAttemptText,
  WorkbookExtraFeedback,
  useAnswerCorrectionLabels,
  WorkbookVerdict,
} from './zine-workbook';

interface FillInBlankExerciseProps {
  content: StudentExerciseContent;
  onExerciseSubmit?: ExerciseSubmitHandler;
  feedback?: ExerciseFeedbackMessage[];
  variant?: ContentDisplayVariant;
  isReadOnly?: boolean;
}

const BLANK_MARKER = '___';

type BlankEvaluation = {
  isCorrect: boolean;
  expected: string;
  provided: string;
  correction?: string;
  grade?: 'perfect' | 'near' | 'partial' | 'wrong';
  reason?: string;
};

export function FillInBlankExercise({
  content,
  onExerciseSubmit,
  feedback,
  variant = 'card',
  isReadOnly = false,
}: FillInBlankExerciseProps) {
  void variant; // the workbook panel is the single presentation for this exercise
  const { t } = useTranslation('p2e');
  const correctionLabels = useAnswerCorrectionLabels();
  const sentences = content.sentences ?? [];
  const lastSubmission = useMemo(() => {
    const submission = content.lastSubmission;
    if (submission?.type === ExerciseType.FillInBlank) {
      return submission;
    }
    return null;
  }, [content.lastSubmission]);
  const lastEvaluationDetails = useMemo(() => {
    const evaluation = (content.lastEvaluation as any) ?? null;
    const autoDetails = (evaluation?.auto as any)?.details ?? evaluation?.details;
    return autoDetails?.kind === 'FillInBlank' ? autoDetails : null;
  }, [content.lastEvaluation]);

  const normalizeAnswers = useCallback(
    (rawAnswers: Array<string | string[] | undefined>): string[][] =>
      sentences.map((sentence, sentenceIndex) => {
        const blanks =
          (sentence.blanks?.length
            ? sentence.blanks
            : [{ answer: (sentence as any)?.answer ?? '' }]) ?? [];
        const provided = rawAnswers?.[sentenceIndex];
        const providedArray = Array.isArray(provided)
          ? provided
          : typeof provided === 'string'
            ? [provided]
            : [];
        return blanks.map((_, blankIndex) => {
          const value = providedArray?.[blankIndex];
          return typeof value === 'string' ? value : '';
        });
      }),
    [sentences]
  );

  const normalizedLastAnswers = useMemo(() => {
    const rawAnswers = lastSubmission?.answers ?? [];
    return normalizeAnswers(rawAnswers as Array<string | string[] | undefined>);
  }, [lastSubmission, normalizeAnswers]);

  const [answers, setAnswers] = useState<string[][]>(() => normalizedLastAnswers);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [answerEvaluations, setAnswerEvaluations] = useState<BlankEvaluation[][]>([]);

  const computeEvaluations = useCallback(
    (submittedAnswers: string[][]) =>
      sentences.map((sentence, sentenceIndex) => {
        const blanks = (sentence.blanks?.length ? sentence.blanks : [{ answer: (sentence as any)?.answer ?? '', correction: (sentence as any)?.correction }]) ?? [];
        const provided = submittedAnswers?.[sentenceIndex] ?? [];

        return blanks.map((blank, blankIndex) => {
          const expectedRaw = typeof blank.answer === 'string' ? blank.answer : '';
          const providedRaw = typeof provided?.[blankIndex] === 'string' ? provided[blankIndex] : '';
          const gradeFromBlank = (blank as any)?.grade as
            | 'perfect'
            | 'near'
            | 'partial'
            | 'wrong'
            | undefined;
          const reasonFromBlank = (blank as any)?.reason as string | undefined;

          const normalize = (value: string) => value.trim().toLocaleLowerCase();
          const expectedNormalized = normalize(expectedRaw);
          const providedNormalized = normalize(providedRaw);

          const isCorrect =
            expectedNormalized.length === 0
              ? providedNormalized.length === 0
              : expectedNormalized === providedNormalized;

          return {
            isCorrect,
            expected: expectedRaw,
            provided: providedRaw,
            correction: blank.correction,
            grade: gradeFromBlank ?? (isCorrect ? 'perfect' : 'wrong'),
            reason: reasonFromBlank,
          };
        });
      }),
    [sentences],
  );

  const evaluationsFromPayload = useMemo(() => {
    if (!lastEvaluationDetails) {
      return null;
    }

    return (lastEvaluationDetails.sentences ?? []).map((sentence: any) => {
      const blanks = Array.isArray(sentence?.blanks) ? sentence.blanks : [];
      return blanks.map((blank: any) => ({
        isCorrect: Boolean(blank?.isCorrect),
        expected: typeof blank?.expectedAnswer === 'string' ? blank.expectedAnswer : '',
        provided: typeof blank?.providedAnswer === 'string' ? blank.providedAnswer : '',
        correction: undefined,
        grade: blank?.grade as 'perfect' | 'near' | 'partial' | 'wrong' | undefined,
        reason: typeof blank?.reason === 'string' ? blank.reason : undefined,
      }));
    });
  }, [lastEvaluationDetails]);

  useEffect(() => {
    if (evaluationsFromPayload) {
      setAnswerEvaluations(evaluationsFromPayload);
    } else if (lastSubmission) {
      setAnswerEvaluations(computeEvaluations((lastSubmission.answers as string[][] | undefined) ?? []));
    } else {
      setAnswerEvaluations([]);
    }
  }, [computeEvaluations, evaluationsFromPayload, lastSubmission]);

  const handleAnswerChange = (sentenceIndex: number, blankIndex: number, value: string) => {
    if (isReadOnly) {
      return;
    }
    setAnswers((previous) => {
      const next = previous.map((sentenceAnswers) => [...sentenceAnswers]);
      if (!next[sentenceIndex]) {
        next[sentenceIndex] = [];
      }
      next[sentenceIndex][blankIndex] = value;
      return next;
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!onExerciseSubmit) {
      return;
    }

    try {
      setIsSubmitting(true);
      await onExerciseSubmit(content, {
        type: ExerciseType.FillInBlank,
        answers,
      });
      // Evaluations update via useEffect when content.lastEvaluation changes after cache invalidation
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = answers.length > 0;

  // "N of M blanks correct" totals for the verdict box
  const blankTotals = useMemo(() => {
    let total = 0;
    let correct = 0;
    answerEvaluations.forEach((sentenceEvaluations) => {
      sentenceEvaluations.forEach((evaluation) => {
        total += 1;
        if (evaluation.isCorrect) {
          correct += 1;
        }
      });
    });
    return { total, correct };
  }, [answerEvaluations]);

  const { verdictMessage, extraMessages } = splitWorkbookFeedback(feedback);
  const verdictOverride =
    verdictMessage?.source === 'auto' && blankTotals.total > 0
      ? verdictMessage.variant === 'success'
        ? `${t('zine.workbook.blanksCorrect', {
            correct: blankTotals.correct,
            total: blankTotals.total,
          })} — ${t('zine.workbook.perfect')}`
        : t('zine.workbook.blanksCorrect', {
            correct: blankTotals.correct,
            total: blankTotals.total,
          })
      : undefined;

  const renderBlankInput = (
    blank: { blankId?: string; answer?: string; correction?: string },
    sentenceIndex: number,
    blankIndex: number,
    key: string,
  ) => {
    const evaluation = answerEvaluations[sentenceIndex]?.[blankIndex];
    const grade = evaluation
      ? evaluation.grade ?? (evaluation.isCorrect ? 'perfect' : 'wrong')
      : null;
    // "near" = accepted by the AI grader (synonym, contraction, tiny typo) — it
    // counts as right in the score, so it shows green too.
    const provided = evaluation?.provided ?? answers[sentenceIndex]?.[blankIndex] ?? '';
    const state: AnswerCheckState = !grade
      ? 'idle'
      : grade === 'perfect' || grade === 'near'
        ? 'right'
        : provided.trim().length === 0
          ? 'empty'
          : 'wrong';
    const expectedLength =
      (evaluation?.expected?.length || (typeof blank.answer === 'string' ? blank.answer.length : 0)) ?? 0;
    const widthCh = Math.min(16, Math.max(6, expectedLength + 2));
    const reason =
      evaluation?.reason || (blank.correction?.trim()?.length ? blank.correction : '');
    const inlineKey = `${content.id}-blank-correction-${sentenceIndex}-${blankIndex}`;
    // The teacher's own wording, alternatives apart: "won't skip / will not skip".
    const correctAnswer = formatAcceptedAnswers(
      typeof blank.answer === 'string' && blank.answer.trim() ? blank.answer : evaluation?.expected,
    );

    return (
      <Fragment key={key}>
        <Blank
          state={state}
          widthCh={widthCh}
          value={answers[sentenceIndex]?.[blankIndex] ?? ''}
          onChange={(event) => handleAnswerChange(sentenceIndex, blankIndex, event.target.value)}
          readOnly={isReadOnly}
          aria-label={`blank ${sentenceIndex + 1}-${blankIndex + 1}`}
        />
        {state !== 'idle' && (
          <AnswerCorrection
            state={state}
            labels={correctionLabels}
            className="mx-1"
            yourAnswer={provided}
            correctAnswer={correctAnswer || undefined}
            reason={reason ? renderInlineMarkdown(reason, `${inlineKey}-reason`) : undefined}
          />
        )}
      </Fragment>
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <WorkbookPanel
        tab={t('zine.workbook.tab.fillIn')}
        title={content.title ? renderInlineMarkdown(content.title, 'fill-in-blank-title') : undefined}
        hint={t('zine.workbook.typeMissing')}
        footer={
          <>
            {onExerciseSubmit && !isReadOnly && (
              <ZineButton type="submit" isLoading={isSubmitting} disabled={!canSubmit || isSubmitting}>
                {isSubmitting ? t('zine.workbook.submitting') : t('zine.workbook.checkAnswers')}
              </ZineButton>
            )}
            {!isReadOnly && <WorkbookAttemptText content={content} />}
            <WorkbookVerdict pending={isSubmitting} message={verdictMessage} overrideText={verdictOverride} />
            <WorkbookExtraFeedback pending={isSubmitting} messages={extraMessages} />
          </>
        }
      >
        <ContentDescription text={content.description} markdown className="mb-3" />

        <div className="mb-4" style={{ lineHeight: 2.6, fontSize: '.95rem' }}>
          {sentences.map((sentence, sentenceIndex) => {
            const blanks = (sentence.blanks?.length
              ? sentence.blanks
              : [{ answer: (sentence as any)?.answer ?? '', correction: (sentence as any)?.correction }]) as Array<{
              blankId?: string;
              position?: number;
              answer: string;
              correction?: string;
            }>;
            const parts = (sentence.sentence ?? '').split(BLANK_MARKER);

            return (
              <p
                key={sentence.sentenceId ?? `${content.id}-sentence-${sentenceIndex}`}
                className={cn('m-0 whitespace-pre-wrap', sentenceIndex > 0 && 'mt-1')}
              >
                {parts.map((part, partIndex) => (
                  <Fragment key={`${sentenceIndex}-part-${partIndex}`}>
                    {part &&
                      renderInlineMarkdown(
                        part,
                        `${content.id}-sentence-${sentenceIndex}-part-${partIndex}`,
                      )}
                    {partIndex < blanks.length &&
                      renderBlankInput(
                        blanks[partIndex]!,
                        sentenceIndex,
                        partIndex,
                        blanks[partIndex]?.blankId ?? `${sentenceIndex}-${partIndex}-${content.id}`,
                      )}
                  </Fragment>
                ))}
                {parts.length - 1 < blanks.length &&
                  blanks.slice(parts.length - 1).map((blank, blankIndex) => {
                    const blankPosition = (parts.length - 1) + blankIndex;
                    return renderBlankInput(
                      blank,
                      sentenceIndex,
                      blankPosition,
                      blank.blankId ?? `${sentenceIndex}-tail-${blankIndex}`,
                    );
                  })}
              </p>
            );
          })}
        </div>
      </WorkbookPanel>
    </form>
  );
}
